/**
 * Flow-specific scene conversion utilities.
 * Duplicated from Scenes for Flow independence. Keep in sync with scene JSON contract.
 */

import type { DeviceID } from 'src/types/DeviceID';
import { DOOR_STATE, INPUTS_READERS, MOTION_DETECTION, SENSOR_DETECTION } from 'src/constants';

/** Inlined from Scenes virtual_component_utils for independence */
const isVCType = (type: string): boolean =>
	['button', 'number', 'boolean', 'enum', 'text'].includes(type);

/**
 * Converts UI property name to Scene Service command format
 * @param name - UI property name (e.g., 'turn', 'brightness', 'temperature')
 * @param ch - Channel number
 * @param mode - Optional device mode
 * @returns Scene Service command string (e.g., 'r1', 'brightness2', 't1')
 */
export const convertUiPropertyToSceneCommand = (
	name: string,
	ch: number | string,
	mode?: string,
): string => {
	const channel = parseInt(String(ch), 10) + 1;

	if (channel > 2000) {
		switch (name) {
			case 'valve_percent_position':
				return `pos${channel}`;
			case 'valve_open_state':
				return `isopen${channel}`;
			case 'target_temp':
				return `tgt${channel}`;
			case 'temperature':
				return `tmp${channel}`;
			case 'battery':
				return `bat${channel}`;
			case 'tmp':
				return `tmp${channel}`;
			case 'trv_target_temperature':
				return `tgt${channel}`;
			case 'boost':
				return `boost${channel}`;
			default:
				return name;
		}
	}

	if (channel > 200 && isVCType(name)) {
		return name + '' + channel;
	}

	if (
		name === 'presence_zone_state' ||
		name === 'presence_zone_objects' ||
		name === 'presence' ||
		name.startsWith('prsn_')
	) {
		return name;
	}

	switch (name) {
		case 'turn':
		case 'turn_add_on':
			return `r${channel}`;
		case 'state':
			return 'state';
		case 'meter':
		case 'power meter':
			return `m${channel}`;
		case 'roller state':
			return 'rl';
		case 'position':
		case 'roller position':
			return 'rlp';
		case 'roller direction':
			return 'rld';
		case 'multi_cover_state':
			return `rl${channel}`;
		case 'multi_cover_position':
			return `rlp${channel}`;
		case 'brightness':
			if (mode === 'color') return 'gain';
			return `brightness${channel}`;
		case 'ct':
			return `ct${channel}`;
		case 'vol_meter':
		case 'vol_meter_add_on':
			return `v${channel}`;
		case 'input_value':
		case 'digital_input_add_on':
			return `i${channel}`;
		case 'push_type':
		case 'push_type_add_on':
			return `ie${channel}`;
		case 'analog_input_add_on':
			return `ip${channel}`;
		case 'temperature':
			if (
				channel > 100 ||
				mode === 'relay' ||
				mode === 'inputs_reader' ||
				mode === 'roller' ||
				mode === 'light' ||
				mode === 'rgbw' ||
				mode === 'rgb' ||
				mode === 'white'
			)
				return `t${channel}`;
			if (mode === 'thermostat') return `tmp${channel}`;
			return 'tmp';
		case 'humidity':
			if (
				channel > 100 ||
				mode === 'relay' ||
				mode === 'inputs_reader' ||
				mode === 'roller' ||
				mode === 'light' ||
				mode === 'rgbw' ||
				mode === 'rgb' ||
				mode === 'white'
			)
				return `h${channel}`;
			return 'hum';
		case 'illumination':
			return 'lux_i';
		case 'luxury':
			return 'lux';
		case 'battery':
			return 'bat';
		case 'distance':
			return 'dist';
		case 'vibration':
			return 'vibr';
		case 'white_channel':
			return 'white';
		case 'adc':
			return `adc${channel}`;
		case 'ext_switch':
			return `es${channel}`;
		case 'total_current':
			return 'mt';
		case 'valve_percent_position':
			return `pos${channel}`;
		case 'valve_open_state':
			return `isopen${channel}`;
		case 'target_temp':
			return `tgt${channel}`;
		case 'set_profile':
			return `sch${channel}`;
		case 'report_window_open':
			return `wo${channel}`;
		case 'boost':
			return `boost${channel}`;
		case 'reported_by':
			return `rep`;
		case 'solar_input_power':
			return 'solarInputPower';
		case 'output_home_power':
			return 'outputHomePower';
		case 'electricLevel':
			return 'electricLevel';
		case 'outputLimit':
		case 'battery_output_limit':
			return 'outputLimit';
		case 'bolt_state':
			return 'bolt_state';
		case 'remote_control_channel':
			return `channel`;
		case 'scroll_position':
			return `rotie1`;
		case 'intensity_left':
			return 'cil';
		case 'intensity_right':
			return 'cir';
		case 'level_left':
			return 'cll';
		case 'level_right':
			return 'clr';
		case 'presence':
			return 'presence';
		default:
			return name;
	}
};

/**
 * Converts UI values to Scene Service value format
 */
export const convertUiValueToSceneValue = (trigger: string, value: unknown): unknown => {
	const triggerValue = trigger.startsWith('isopen') ? 'isopen' : trigger;

	switch (triggerValue) {
		case 'door':
		case 'window':
			return DOOR_STATE.find((el) => value === el.ui)?.check || 'on';
		case 'motion':
		case 'vibr':
		case 'pir':
			return MOTION_DETECTION.find((el) => el.ui === value)?.check || 'on';
		case 'ie1':
		case 'ie2':
		case 'ie3':
		case 'ie4':
		case 'ie5':
		case 'ie6':
		case 'ie7':
		case 'ie8':
			return INPUTS_READERS.find((el) => el.ui === value)?.check || 'S';
		case 'flood':
		case 'smoke':
		case 'gas':
		case 'moisture_alarm':
			return SENSOR_DETECTION.find((el) => el.ui === value)?.check || 'false';
		case 'ppm':
			return String(value);
		case 'isopen':
			return value === 'open' ? 'on' : 'off';
		case 'channel':
			if (value === 'any') return 'any';
			return Number(String(value).split('_')[1]);
		case 'remote_control_conditions':
			return {
				push_type: (value as any)?.push_type || 'short',
				remote_control_channel: (value as any)?.remote_control_channel || 'any',
				scroll_position: (value as any)?.scroll_position || 'any',
			};
		case 'scroll_position':
			return Number(String(value).split('_')[1]);
		default:
			if (typeof trigger === 'string' && trigger.match(/ie[0-9]c/)) {
				const m = trigger.match(/ie([0-9])c/);
				return m ? `S${m[1]}` : value;
			}
			if (typeof trigger === 'string' && trigger.match(/rotie[0-9]c/)) {
				const m = trigger.match(/rotie([0-9])c/);
				return m ? `R${m[1]}` : value;
			}
			if (typeof trigger === 'string' && trigger.match(/ie[0-9]{3}/)) {
				return INPUTS_READERS.find((el) => el.ui === value)?.check || 'S';
			}
			if (
				(typeof trigger === 'string' &&
					(trigger.match(/^r[0-9]+$/) ||
						trigger === 'state' ||
						trigger.match(/^turn[0-9]+$/))) ||
				triggerValue === 'isopen'
			) {
				const v = String(value).toLowerCase();
				if (v === 'on' || v === 'true') return '1';
				if (v === 'off' || v === 'false') return '0';
			}
			return value;
	}
};

/**
 * Create a device_id string from a deviceID object
 */
export function createGuiDeviceId(deviceID: DeviceID): string {
	if (Number(deviceID.channel) > 0) {
		return `${deviceID.id}_${deviceID.channel}`;
	}
	return `${deviceID.id}`;
}
